/**
 * craineCaseKnowledge.js
 *
 * Phase-gated chunked case knowledge for Craine's briefing context. Instead of
 * injecting the full case backstory into every prompt (~1,200 tokens), we split
 * it into labeled chunks and retrieve only what's relevant to the player's input
 * AND the current Craine phase.
 *
 * This serves three purposes:
 * 1. Token savings — most turns inject 0 chunks (player already knows the case)
 * 2. Stable prefix — the prompt's first ~3K tokens stay identical across turns,
 *    letting Gemini's implicit prefix caching kick in (auto-discount)
 * 3. Phase integrity — Craine never references knowledge he has not yet acquired
 *
 * Phase gating reads from the npc-phase-bible's craineCaseKnowledgeChunks arrays,
 * bridging the JSON knowledge plane into the runtime retrieval path. When no phase
 * is supplied (backward compatibility), all chunks are available.
 *
 * The classifier uses the same keyword-matching pattern as interference.js.
 */
import { PHASE_CHUNK_IDS } from './crainePhaseAllowlists.js';
import { getCaseKnowledge } from './activeChapter.js';

// ── Phase Chunk Allowlists ──────────────────────────────────────────────────
// Map from phaseId letter to the set of chunk IDs that phase may receive. The
// arrays live in crainePhaseAllowlists.js (no prose, no spoilers) so the full
// npc-phase-bible.json no longer ships in the client bundle. The prompt-sync
// gate keeps the client copy, the server copy, and the bible identical.
const PHASE_ALLOWLISTS = {};
for (const phase of Object.keys(PHASE_CHUNK_IDS)) {
  PHASE_ALLOWLISTS[phase] = new Set(PHASE_CHUNK_IDS[phase]);
}

// ── Knowledge Chunks ─────────────────────────────────────────────────────────
// CHUNKS data has moved to content/chapters/chapter1/knowledge.js so it is
// emitted in the chapter chunk. Access it at runtime via getCaseKnowledge().

export const CANONICAL_IDS = {
  evidenceIds: ['COURIER_MANIFEST', 'CHIP_DONATION_TIMING', 'ST_ERASMUS_ROUTING', 'NETWORK_PING', 'CASE_FILE_RENA_DELACROIX', 'DELACROIX_NOTEBOOK'],
  characterIds: ['elias-craine', 'victor-hargrove'],
};

// ── Phase-dependent "do not know" blocks ─────────────────────────────────────
// In Phase A/B, Craine does not know about LUMEN-0 or SECTOR_7F.
// In Phase C, SECTOR_7F is revealed but identities remain unknown.
// In Phase D, the full conspiracy is known.
const DO_NOT_KNOW = {
  A: `
WHAT YOU DO NOT KNOW (do NOT invent or reveal):
- You do NOT know the killer's name or identity
- You do NOT know the chip is sabotaged or has interference protocols
- You do NOT know about LUMEN-0, Project Insight, or SECTOR_7F
- You do NOT know Helios's chip caused the murders
- You suspect Helios is connected. You do not know HOW.`,
  B: `
WHAT YOU DO NOT KNOW (do NOT invent or reveal):
- You do NOT know the killer's name or identity
- You do NOT know the chip is sabotaged or has interference protocols
- You do NOT know about LUMEN-0, Project Insight, or SECTOR_7F
- You do NOT know Helios's chip caused the murders
- You suspect Helios is connected. You do not know HOW.`,
  C: `
WHAT YOU DO NOT KNOW (do NOT invent or reveal):
- You do NOT know the killer's name or identity
- You do NOT know the killer's identity or condition
- You do NOT know the full scope of the conspiracy
- You DO know the chip has SECTOR_7F interference, but you do not know who installed it or why beyond Helios obstruction
- You suspect someone inside Helios is directing the murders. You do not know WHO.`,
  D: `
WHAT YOU STILL DO NOT KNOW (do NOT invent):
- Specific details beyond what the evidence has revealed
- The endgame outcome (the player chooses)`,
};

// ── Keyword Classifier ───────────────────────────────────────────────────────
// Maps patterns to chunk IDs. Multiple patterns can trigger the same chunk.
// Ordered roughly by specificity. A single input can trigger multiple chunks.

const TRIGGERS = [
  // Full briefing triggers (memory loss, "tell me everything")
  { patterns: [/can'?t remember/i, /lost (my |the )?memory/i, /don'?t (know|remember) (anything|the case|what)/i, /brief me/i, /run me through/i, /tell me (everything|about the case|what we know)/i, /what('?s| is) (the|this) case/i, /start from (the )?(beginning|scratch|top)/i, /fill me in/i, /what do we know/i, /what happened/i, /totally blank/i, /catch me up/i, /where are we/i, /what('?re| are) we (doing|working|investigating)/i, /what'?s going on/i, /no (idea|clue) what/i, /refresh my memory/i], chunks: 'ALL' },

  // Specific retrievals
  { patterns: [/victim/i, /dead/i, /killed/i, /nadia/i, /sorel/i, /yelena/i, /marsh/i, /celeste/i, /women/i, /murders?/i, /bodies/i, /toxin/i, /symbol/i], chunks: ['case_overview', 'case_overview_phase_c'] },
  { patterns: [/suspect/i, /killer/i, /who (did|is doing)/i, /witness/i, /thin/i, /brown hair/i, /maintenance/i, /ghost/i, /left.?hand/i], chunks: ['suspect_composite'] },
  { patterns: [/pattern/i, /triangle/i, /vasik/i, /alchemical/i, /philosopher/i, /fifth/i, /five/i, /incomplete/i, /geometric/i], chunks: ['geographic_pattern'] },
  { patterns: [/chip/i, /lumen/i, /implant/i, /neural/i, /phone.? home/i, /ping/i, /handshake/i], chunks: ['chip_context', 'chip_compromised'] },
  { patterns: [/hargrove/i, /victor/i, /ceo/i, /helios/i, /who (is|are) (he|we (seeing|visiting|talking))/i], chunks: ['hargrove_context'] },
  { patterns: [/courier/i, /manifest/i, /reclassif/i, /yelena/i, /marsh/i, /pickup/i, /log/i], chunks: ['thread_courier'] },
  { patterns: [/donat/i, /timing/i, /fast/i, /two days/i, /september/i, /ready/i, /pre.?position/i, /why.*gave/i], chunks: ['thread_donation'] },
  { patterns: [/trial/i, /clinical/i, /erasmus/i, /hospital/i, /subjects/i, /results/i, /rena/i, /beacon/i, /journal/i, /medical.?data/i], chunks: ['thread_trial'] },
  { patterns: [/plan/i, /strategy/i, /approach/i, /what do we do/i, /how (do|should) we/i, /what.?angle/i, /go back in/i], chunks: ['interview_plan'] },
  // Phase C/D triggers
  { patterns: [/rena/i, /delacroix/i, /fourth/i, /4th/i, /calloway/i, /retired/i, /forensic tech/i], chunks: ['rena_victim'] },
  { patterns: [/sector/i, /7f/i, /interference/i, /sabotage/i, /overwrit/i, /suppress/i, /compromised/i, /malfunction/i, /headache/i, /blackout/i], chunks: ['chip_compromised'] },
  { patterns: [/fifth/i, /next (victim|target|one)/i, /who'?s next/i, /save/i, /protect/i, /at risk/i, /11.?day/i, /eleven.?day/i, /interval/i], chunks: ['fifth_at_risk'] },
];

/**
 * Given the player's latest input (and optionally recent history),
 * returns an array of chunk objects to inject into the prompt.
 * Returns [] if nothing triggers — the common case.
 *
 * When a phase is provided, only chunks allowed by the npc-phase-bible's
 * craineCaseKnowledgeChunks for that phase are returned. This bridges the
 * JSON knowledge plane into the retrieval path.
 *
 * @param {string} input - The player's current transmission
 * @param {string[]} [recentInputs] - Last 2-3 player inputs for broader context
 * @param {string} [phase] - Craine's current phase letter (A/B/C/D). Omit for unfiltered.
 * @returns {object[]} Array of chunk objects from CHUNKS
 */
export function retrieveChunks(input, recentInputs = [], phase = null) {
  const combined = [input, ...recentInputs].join(' ');
  const triggered = new Set();
  const allowlist = phase ? PHASE_ALLOWLISTS[phase] : null;

  for (const rule of TRIGGERS) {
    const matched = rule.patterns.some((p) => p.test(combined));
    if (!matched) continue;

    if (rule.chunks === 'ALL') {
      const all = Object.values(getCaseKnowledge());
      return allowlist ? all.filter((c) => allowlist.has(c.id)) : all;
    }
    rule.chunks.forEach((id) => triggered.add(id));
  }

  let results = [...triggered].map((id) => getCaseKnowledge()[id]).filter(Boolean);
  if (allowlist) {
    results = results.filter((c) => allowlist.has(c.id));
  }
  return results;
}

/**
 * Formats retrieved chunks into a prompt-injectable string.
 * Returns empty string if no chunks retrieved (zero token cost).
 *
 * When a phase is provided, the "WHAT YOU DO NOT KNOW" block adjusts to
 * match the knowledge boundaries defined in the character-bible for that
 * Craine phase. This prevents the prompt from denying knowledge Craine
 * has already acquired (e.g., SECTOR_7F in Phase C/D).
 *
 * @param {object[]} chunks - Array from retrieveChunks()
 * @param {string} [phase] - Craine's current phase letter (A/B/C/D). Defaults to 'B' (demo).
 * @returns {string}
 */
export function formatChunksForPrompt(chunks, phase = 'B') {
  if (!chunks.length) return '';
  const body = chunks.map((c) => `[${c.label}]\n${c.text}`).join('\n\n');
  const doNotKnow = DO_NOT_KNOW[phase] || DO_NOT_KNOW.B;
  return `
CANONICAL CASE FACTS (ground truth — use ONLY these names, relationships, and details):

${body}

HARD CONSTRAINT: The victim names, causes of death, and relationships above are EXHAUSTIVE and EXACT. Do not invent, substitute, or add any victims, suspects, names, causes of death, or family relationships beyond what is stated above. If the chip asks about something not covered here, say you don't have that information yet.
${doNotKnow}
`;
}
